import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { VIEWER_MODES } from 'constants/all';

import { ViewerHostContractsProvider } from 'modules/common/viewer/hostContracts/ViewerHostContractsProvider';
import type { ViewerAccess } from 'modules/common/viewer/hostContracts/access';
import {
  LOADING,
  locked,
  partLockFromFlags,
  UNAVAILABLE,
  UNLOCKED,
  type ViewerEditLocks,
} from 'modules/common/viewer/hostContracts/editLocks';

import { getActiveRootFile } from './data/reducer';

const OWNER_ACCESS: ViewerAccess = { relationship: VIEWER_MODES.OWNER };

/**
 * Standalone file viewer composition root.
 *
 * The standalone viewer payload's `quoteContext` carries the root file's part
 * flags and geometry lock, so the part lock is built from the same flags as on
 * the other viewers. It carries no collaboration lock, so that is unavailable.
 */
export const SfvViewerContractsRoot = ({
  children,
}: {
  children: ReactNode;
}) => {
  const rootFile = useSelector(getActiveRootFile);

  const editLocks = useMemo<ViewerEditLocks>(() => {
    if (!rootFile) {
      return {
        partLock: LOADING,
        geometryLock: LOADING,
        collaborationLock: UNAVAILABLE,
      };
    }
    const { quoteContext } = rootFile;
    return {
      partLock: quoteContext ? partLockFromFlags(quoteContext) : UNAVAILABLE,
      geometryLock: quoteContext?.geometryLockReason
        ? locked(quoteContext.geometryLockReason)
        : UNLOCKED,
      collaborationLock: UNAVAILABLE,
    };
  }, [rootFile]);

  return (
    <ViewerHostContractsProvider editLocks={editLocks} access={OWNER_ACCESS}>
      {children}
    </ViewerHostContractsProvider>
  );
};
